import React, { useEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DialpadIcon from '@mui/icons-material/Dialpad';
import TextField from '@mui/material/TextField';
import Collapse from '@mui/material/Collapse';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from '@mui/icons-material/BackspaceOutlined';
import CallingFrom from './components/CallingFrom';
import DialPad from './components/DialPad';
import ContactsList from './components/ContactsList';
import useAbortController from 'hooks/useAbortController';
import { getContacts } from 'services/contacts.service';
import { snack } from 'utils/toast';
import { useSelector } from 'react-redux';

const limit = 10;

const Dialer = ({ onCall, params, onClose }) => {
  const [fromNumber, setFromNumber] = useState('');
  const [query, setQuery] = useState(params.number ?? '');
  const [showDialer, setShowDialer] = useState(false);
  const inputRef = useRef();
  const [data, setData] = useState({
    data: [],
    loading: true,
    hasMore: true,
    offset: 0,
  });

  useEffect(() => {
    if (params.number) {
      setQuery(params.number);
    }
  }, [params.number]);

  const { newAbortSignal, cancelPreviousRequest, isCancel } =
    useAbortController();
  const account = useSelector((s) => s.settings.account);

  const toggleDialer = () => {
    setShowDialer(!showDialer);
  };

  const onQueryChange = (val) => {
    setQuery(val);
    inputRef.current?.focus();
  };

  useEffect(() => {
    cancelPreviousRequest();
    setData({ ...data, loading: true, hasMore: false });
    loadContacts(
      {
        offset: 0,
        having_phone: 1,
        signal: newAbortSignal(),
      },
      true,
    );
  }, [query]);

  const loadContacts = (options, reset) => {
    const contactQuery = {
      ...options,
      limit,
      having_phone: 1,
      search: query,
      account_id: account.account_id,
    };
    return getContacts(contactQuery)
      .then((resp) => {
        // const options = resp.map((i) => ({
        //   label: getName(i),
        //   value: i.contact_id,
        // }));
        return {
          options: resp,
          hasMore: resp.length >= limit,
        };
      })
      .then((resp) => {
        setData((prev) => {
          const data = reset ? resp.options : [...prev.data, ...resp.options];
          return {
            ...prev,
            data,
            offset: options.offset,
            hasMore: resp.hasMore,
            loading: false,
          };
        });
      })
      .catch((err) => {
        if (!isCancel(err)) {
          snack.error(err.message);
        }
        setData((prev) => ({ ...prev, loading: false }));
      });
  };

  const onLoadMore = () => {
    if (data.loading || !data.hasMore) return;
    const offset = data.offset + limit;
    setData((prev) => ({ ...prev, loading: true, hasMore: false })); // set hasMore to false to prevent multiple requests
    loadContacts({ offset });
  };

  const onBackspace = () => {
    setQuery(query.slice(0, -1));
  };

  const onContactSelect = (contact) => {
    return onCall(contact.primary_phone, fromNumber, {
      contact_first_name: contact.contact_first_name,
      contact_last_name: contact.contact_last_name,
      primary_phone: contact.primary_phone,
    });
  };

  return (
    <Stack height={500}>
      <Box p={2}>
        <CallingFrom
          fromNumber={fromNumber}
          onChange={setFromNumber}
          onClose={onClose}
        />
        <TextField
          fullWidth
          inputRef={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          margin="none"
          variant="standard"
          placeholder="Start typing name or number"
          inputProps={{
            style: {
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: 500,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onBackspace}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <ContactsList
        contacts={data.data}
        onLoadMore={onLoadMore}
        hasMore={data.hasMore}
        onClick={onContactSelect}
        onHideDialer={() => setShowDialer(false)}
      />
      <Collapse in={showDialer}>
        <DialPad
          canDial={!!fromNumber && !!query}
          onDial={(number) => onCall(number, fromNumber, {})}
          onChange={onQueryChange}
          value={query}
        />
      </Collapse>
      {!showDialer && (
        <Box mt="auto" bgcolor="background.default" textAlign="center" p={2}>
          <IconButton onClick={toggleDialer} color="primary">
            <DialpadIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            Click to open dialpad
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default Dialer;
